import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Send, Search, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Input } from '@/components/ui/input.jsx';
import { useLicenseList, useLicenseMe } from '@/hooks/useLicenses.js';
import { licenseStatus, fmtDate } from '@/lib/license-status.js';
import { stateName } from '@/lib/us-states.js';
import UploadLicenseDialog from '@/components/licenses/UploadLicenseDialog.jsx';
import CarrierExportDialog from '@/components/licenses/CarrierExportDialog.jsx';
import PdfViewerDialog from '@/components/licenses/PdfViewerDialog.jsx';

const STATUS_FILTERS = [
  { key: 'current', label: 'Current' },
  { key: 'expiring', label: 'Expiring' },
  { key: 'expired', label: 'Expired' },
  { key: 'perpetual', label: 'No expiry' },
];

export default function LicensesAdmin() {
  const navigate = useNavigate();
  const me = useLicenseMe();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'producer' ? 'producer' : 'agency';
  const [scopeAll, setScopeAll] = useState(false);
  const [q, setQ] = useState('');
  const [statusOn, setStatusOn] = useState(() => new Set(STATUS_FILTERS.map((s) => s.key)));
  const [addOpen, setAddOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [viewing, setViewing] = useState(null); // { kind, id, title }

  const { rows, error, loading, refresh } = useLicenseList(tab, {
    scope: tab === 'producer' && scopeAll ? 'all' : undefined,
  });

  function setTab(next) { setParams((p) => { p.set('tab', next); return p; }, { replace: true }); }
  function toggleStatus(key) {
    setStatusOn((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  // Default sort: soonest-expiring first (expired at top), perpetual last.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).map((r) => {
      const st = licenseStatus(r.expiration_date);
      const hay = [r.state, stateName(r.state), r.license_number, r.drlp_name_snapshot, r.broker_name_snapshot, r.npn, r.license_class]
        .filter(Boolean).join(' ').toLowerCase();
      return { ...r, _status: st, _dim: !statusOn.has(st.key), _hidden: !!needle && !hay.includes(needle) };
    }).filter((r) => !r._hidden)
      // Alphabetical by full state name — matches the server, export PDF, and
      // plain-text. Urgency surfaces via the status chips, not row order.
      .sort((a, b) => stateName(a.state).localeCompare(stateName(b.state)));
  }, [rows, q, statusOn]);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text">Licenses</h2>
          <p className="mt-1 text-sm text-text-muted">Agency &amp; producer state licenses · renewals · carrier export</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExportOpen(true)}><Send size={16} strokeWidth={1.5} /> Export to carrier…</Button>
          <Button onClick={() => setAddOpen(true)}><Plus size={16} strokeWidth={1.5} /> Add license</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 flex gap-1 border-b border-border">
        {['agency', 'producer'].map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={['-mb-px border-b-2 px-3 py-2 text-sm capitalize',
              tab === t ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-text'].join(' ')}>
            {t}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <Input className="pl-9" placeholder="state, license #, broker…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {STATUS_FILTERS.map((s) => (
          <button key={s.key} type="button" onClick={() => toggleStatus(s.key)}
            className={['rounded-pill border px-2.5 py-1 text-xs',
              statusOn.has(s.key) ? 'border-accent/50 bg-accent-soft text-accent' : 'border-border text-text-dim'].join(' ')}>
            {s.label}
          </button>
        ))}
        {tab === 'producer' && me?.is_admin && (
          <button type="button" onClick={() => setScopeAll((v) => !v)}
            className={['rounded-pill border px-2.5 py-1 text-xs',
              scopeAll ? 'border-accent/50 bg-accent-soft text-accent' : 'border-border text-text-muted'].join(' ')}>
            {scopeAll ? 'All brokers' : 'My licenses'}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="mt-4">
        {loading && !rows && <div className="rounded-card border border-border bg-surface p-6 text-sm text-text-muted">Loading…</div>}
        {error && !rows && (
          <div className="rounded-card border border-accent/40 bg-accent-soft p-4 text-sm text-accent">
            Couldn't load licenses: {error.body?.error || error.code || error.message}
          </div>
        )}
        {rows && filtered.length === 0 && (
          <div className="rounded-card border border-dashed border-border p-6 text-center text-sm text-text-muted">
            No {tab} licenses{q ? ' match your search' : ' yet'}.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-card border border-border bg-surface">
            {tab === 'agency'
              ? <AgencyTable rows={filtered} onRow={(id) => navigate(`/admin/licenses/agency/${id}`)} onView={setViewing} />
              : <ProducerTable rows={filtered} onRow={(id) => navigate(`/admin/licenses/producer/${id}`)} onView={setViewing} />}
          </div>
        )}
      </div>

      <UploadLicenseDialog open={addOpen} onOpenChange={setAddOpen} kind={tab} me={me} onSaved={refresh} />
      <CarrierExportDialog open={exportOpen} onOpenChange={setExportOpen} me={me} />
      <PdfViewerDialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)} kind={viewing?.kind} id={viewing?.id} title={viewing?.title} />
    </div>
  );
}

function ViewBtn({ row, kind, onView }) {
  if (!row.workdrive_file_id) return <Badge variant="dashed">No PDF</Badge>;
  return (
    <button type="button" title="View PDF"
      onClick={(e) => { e.stopPropagation(); onView({ kind, id: row.id, title: `${stateName(row.state)} — ${row.license_number}` }); }}
      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-text-muted hover:border-accent/50 hover:text-accent">
      <FileText size={13} strokeWidth={1.5} /> PDF
    </button>
  );
}

function HeadRow({ tmpl, labels }) {
  return (
    <div className="grid gap-2 border-b border-border bg-surface-strong/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-dim" style={{ gridTemplateColumns: tmpl }}>
      {labels.map((l) => <span key={l}>{l}</span>)}
    </div>
  );
}

function Row({ tmpl, dim, onClick, children }) {
  return (
    <div role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      className={['grid w-full cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-surface-strong/50',
        dim ? 'opacity-45' : ''].join(' ')}
      style={{ gridTemplateColumns: tmpl }}>
      {children}
    </div>
  );
}

function AgencyTable({ rows, onRow, onView }) {
  const tmpl = '1.4fr 1.1fr 1.1fr 6.5rem 6.5rem 6rem 4.5rem';
  return (
    <div>
      <HeadRow tmpl={tmpl} labels={['State', 'License #', 'DRLP', 'Issued', 'Expires', 'Status', 'PDF']} />
      {rows.map((r) => (
        <Row key={r.id} tmpl={tmpl} dim={r._dim} onClick={() => onRow(r.id)}>
          <span className="font-medium text-text">{stateName(r.state)}</span>
          <span className="truncate text-text-muted">{r.license_number}</span>
          <span className="truncate text-text-muted">{r.drlp_name_snapshot || '—'}</span>
          <span className="text-text-dim">{fmtDate(r.issue_date)}</span>
          <span className="text-text-dim">{r.expiration_date ? fmtDate(r.expiration_date) : 'Perpetual'}</span>
          <span><Badge variant={r._status.variant}>{r._status.label}</Badge></span>
          <span><ViewBtn row={r} kind="agency" onView={onView} /></span>
        </Row>
      ))}
    </div>
  );
}

function ProducerTable({ rows, onRow, onView }) {
  const tmpl = '1.2fr 1.2fr 1fr 5rem 4rem 6.5rem 5.5rem 4.5rem';
  return (
    <div>
      <HeadRow tmpl={tmpl} labels={['Broker', 'State', 'License #', 'NPN', 'Res?', 'Expires', 'Status', 'PDF']} />
      {rows.map((r) => (
        <Row key={r.id} tmpl={tmpl} dim={r._dim} onClick={() => onRow(r.id)}>
          <span className="truncate text-text">{r.broker_name_snapshot}</span>
          <span className="font-medium text-text">{stateName(r.state)}</span>
          <span className="truncate text-text-muted">{r.license_number}</span>
          <span className="text-text-dim">{r.npn || '—'}</span>
          <span className="text-text-dim">{r.is_resident ? 'Yes' : 'No'}</span>
          <span className="text-text-dim">{r.expiration_date ? fmtDate(r.expiration_date) : 'Perpetual'}</span>
          <span><Badge variant={r._status.variant}>{r._status.label}</Badge></span>
          <span><ViewBtn row={r} kind="producer" onView={onView} /></span>
        </Row>
      ))}
    </div>
  );
}
